import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { authService } from '../services/authService';
import { User } from '../types/user';
import tokenStore from '../stores/tokenStore';

// --- Tipi e Interfacce ---
interface AuthContextType {
    user: User | null;
    loading: boolean;
    login: (email: string, password: string) => Promise<{ two_fa_required: boolean }>;
    logout: () => void;
    loginWithUser: (user: User) => void;
}

interface LoginResponseData {
    access_token: string;
    refresh_token: string;
    user: User;
}

interface TwoFactorResponseData {
    "2fa_token": string; // Il backend usa "2fa_token", non "two_fa_token"
    message?: string;
}

// Creiamo un tipo union per gestire entrambi i formati possibili
interface TwoFactorResponseFlexible {
    "2fa_token"?: string;
    "two_fa_token"?: string;
    message?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth deve essere usato in un AuthProvider');
    return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const initAuth = async () => {
            const token = localStorage.getItem('access_token');
            if (token) {
                try {
                    const userData = await authService.getCurrentUser();
                    setUser(userData);
                } catch (error) {
                    authService.logout();
                }
            }
            setLoading(false);
        };
        initAuth();
    }, []);

    const login = async (email: string, password: string): Promise<{ two_fa_required: boolean }> => {
        try {
            const response = await authService.login(email, password);
            
            // Verifica se è richiesta la 2FA (status 206)
            if (response.status === 206) {
                let responseData = response.data;
                
                // Se la risposta è una stringa, prova a fare il parsing
                if (typeof responseData === 'string') {
                    try {
                        responseData = JSON.parse(responseData);
                    } catch (e) {
                        throw new Error('Risposta dal server non valida.');
                    }
                }
                
                // Controlla se i dati hanno la struttura corretta
                const twoFaData = responseData as TwoFactorResponseFlexible;
                
                // Prova entrambi i possibili nomi del campo
                const token = twoFaData["2fa_token"] || twoFaData["two_fa_token"];
                
                if (token) {
                    // Salva il token in entrambi i posti per sicurezza
                    tokenStore.setToken(token);
                    sessionStorage.setItem('2fa_token', token);
                    
                    return { two_fa_required: true };
                } else {
                    throw new Error('Token 2FA non ricevuto dal server.');
                }
            } else {
                // Login normale senza 2FA
                const loginData = response.data as LoginResponseData;
                localStorage.setItem('access_token', loginData.access_token);
                localStorage.setItem('refresh_token', loginData.refresh_token);
                setUser(loginData.user);
                return { two_fa_required: false };
            }
        } catch (error: any) {
            // Se l'errore ha una risposta con status 206, potrebbe essere comunque una richiesta 2FA
            if (error.response && error.response.status === 206) {
                const responseData = error.response.data as TwoFactorResponseFlexible;
                
                const token = responseData["2fa_token"] || responseData["two_fa_token"];
                if (responseData && token) {
                    tokenStore.setToken(token);
                    sessionStorage.setItem('2fa_token', token);
                    return { two_fa_required: true };
                }
            }
            
            throw error;
        }
    };

    const logout = () => {
        authService.logout();
        tokenStore.clearToken();
        setUser(null);
    };

    const loginWithUser = (loggedInUser: User) => {
        setUser(loggedInUser);
    };

    const value = { user, loading, login, logout, loginWithUser };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};